import { useQuery } from '@tanstack/react-query'
import { createClient } from '@supabase/supabase-js'

// Use backend's environment variable naming convention
const supabaseUrl = import.meta.env.VITE_BLOKADE_MAP_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_BLOKADE_MAP_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface TweetTableData {
  id: number;
  tweet_id: string;
  text: string;
  author_username: string;
  author_name: string;
  created_at: string;
  like_count: number;
  retweet_count: number;
  reply_count: number;
  place_name: string | null;
  nearest_city: string | null;
  category: string;
  language: string | null;
  media_urls?: any;  // Added for media support
  hashtags?: string[];
  is_reply?: boolean;
  
  // Promoted tweet fields
  is_promoted?: boolean;
  promoted_by_tweet_id?: string;
  promoted_by_username?: string;
  promoted_at?: string;
  original_tweet_id?: string;
  original_tweet_text?: string;
  original_author_username?: string;
  original_author_name?: string;
  original_media_urls?: any;
}

// Convert database tweet to table format
function convertToTableData(tweet: any): TweetTableData {
  // Parse media_urls if it's a string
  let mediaUrls = tweet.media_urls;
  if (typeof mediaUrls === 'string') {
    try {
      mediaUrls = JSON.parse(mediaUrls);
    } catch (e) {
      mediaUrls = [];
    }
  }
  
  // Ensure media_urls is an array
  if (!Array.isArray(mediaUrls)) {
    mediaUrls = [];
  }
  
  // Parse original_media_urls if it's a string (for promoted tweets)
  let originalMediaUrls = tweet.original_media_urls;
  if (typeof originalMediaUrls === 'string') {
    try {
      originalMediaUrls = JSON.parse(originalMediaUrls);
    } catch (e) {
      originalMediaUrls = [];
    }
  }
  
  // Ensure original_media_urls is an array
  if (!Array.isArray(originalMediaUrls)) {
    originalMediaUrls = originalMediaUrls || [];
  }

  return {
    id: parseInt(tweet.id, 10),
    tweet_id: tweet.tweet_id,
    text: tweet.tweet_text,
    author_username: tweet.author_username,
    author_name: tweet.author_name || tweet.author_username,
    created_at: tweet.tweet_created_at || tweet.created_at,
    like_count: tweet.like_count || 0,
    retweet_count: tweet.retweet_count || 0,
    reply_count: tweet.reply_count || 0,
    place_name: tweet.place_name || null,
    nearest_city: tweet.nearest_city || null,
    category: 'blockade',
    language: 'sr',
    hashtags: Array.isArray(tweet.hashtags) ? tweet.hashtags : [],
    media_urls: mediaUrls,
    is_reply: tweet.is_reply || false,
    // Add promoted tweet fields
    is_promoted: tweet.is_promoted || false,
    promoted_by_tweet_id: tweet.promoted_by_tweet_id,
    promoted_by_username: tweet.promoted_by_username,
    promoted_at: tweet.promoted_at,
    original_tweet_id: tweet.original_tweet_id,
    original_tweet_text: tweet.original_tweet_text,
    original_author_username: tweet.original_author_username,
    original_author_name: tweet.original_author_name,
    original_media_urls: originalMediaUrls
  }
}

export function useAllTweets() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['all-tweets'],
    queryFn: async () => {
      const { data, error } = await createClient(supabaseUrl, supabaseAnonKey)
        .from('tweets_geo')
        .select('*')
        .order('tweet_created_at', { ascending: false })
        .limit(5000)  // Increased to 5000

      if (error) throw error
      
      // Debug log to see raw data structure
      if (data && data.length > 0) {
        console.log('Sample raw tweet data:', data[0])
        console.log('media_urls type:', typeof data[0].media_urls, data[0].media_urls)
        console.log('hashtags type:', typeof data[0].hashtags, data[0].hashtags)
      }
      
      return (data || []).map(convertToTableData)
    },
    refetchInterval: 30000,  // Refresh every 30 seconds
    refetchIntervalInBackground: true
  })

  return {
    data: data || [],
    isLoading,
    error
  }
} 